<template>
  <div class="linexo main-container lnx-row">
    <section id="lnx-calculator" class="lnx-col-12 lnx-col-lg-8">
      <OStepper v-if="currentStep" :current-step="currentStep" :is-loading="formSchemaLoading" :steps="steps" />
    </section>
    <section id="lnx-result" class="linexo ocean-theme lnx-col-12 lnx-col-lg-4">
      <OResult
        :has-valid-result="isCalculationResultValid && isFormDataValid"
        :is-loading="isLoading"
        :message="resultErrorMessage ? $t(resultErrorMessage) : undefined"
        :price="price"
        :savings="savings"
        :savings-percentage="savingsPercentage"
        @detail-button-clicked="onDetailButtonClicked"
      />
    </section>

    <component
      :is="isMobile ? MDetailTableMobile : MDetailTable"
      v-if="isDetailTableActive"
      v-bind="{
        price,
        savings,
        savingsPercentage,
        showDetailsTable: isDetailTableActive,
        privateBuy,
        leasing
      }"
      @update:show-details-table="showDetailsTable"
    />

    <AInfoModal />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useFormStore } from '@/stores/formStore.ts'
import { useGlobalStore } from '@/stores/globalStore.ts'
import { useCalculationStore } from '@/stores/calculationStore.ts'

import OStepper from '@/components/organisms/o-stepper/O-Stepper.vue'
import OResult from '@/components/organisms/result/O-Result.vue'
import AInfoModal from '@/components/atoms/modal/A-InfoModal.vue'
import MDetailTable from '@/components/molecules/detail-table/M-DetailTable.vue'
import MDetailTableMobile from '@/components/molecules/detail-table/M-DetailTableMobile.vue'
import { useBreakpoint } from '@/composables/useBreakpoint'

const formStore = useFormStore()

const { currentStep, steps, formSchemaLoading } = storeToRefs(formStore)
const { isLoading, hasApiError, isDetailTableActive } = storeToRefs(useGlobalStore())

const { showDetailsTable } = useGlobalStore()
const { calculatedResults, errors: calculationErrors } = storeToRefs(useCalculationStore())

const price = computed(() => calculatedResults.value?.monthlyNetBurden ?? 0)
const savings = computed(() => calculatedResults.value?.absoluteSavings ?? 0)
const savingsPercentage = computed(() => calculatedResults.value?.relativeSavings ?? 0)
const privateBuy = computed(() => calculatedResults.value?.private_buy)
const leasing = computed(() => calculatedResults.value?.leasing)

const { isMobile } = useBreakpoint()

const isCalculationResultValid = computed(
  () =>
    !calculationErrors.value?.length &&
    calculatedResults.value.monthlyNetBurden !== null &&
    calculatedResults.value.absoluteSavings !== null
)

const isFormDataValid = computed(() => currentStep.value?.valid === null || !!currentStep.value?.valid)

const resultErrorMessage = computed(() => {
  if (hasApiError.value) {
    return 'error.api_error'
  }
  return !isFormDataValid.value ? 'error.check_settings' : undefined
})

function onDetailButtonClicked(value: boolean) {
  showDetailsTable(value)
}
</script>

<style lang="scss" scoped>
@import 'calculation-view';
</style>
